import type { LayoutWidgetPos } from './infrastructure/contracts/layout.contract';
import type { ViewLevel } from './dashboard-resolution';

export interface DashboardViewLayout {
  id: string;
  level: ViewLevel;
  /** Undefined = the level's base layout, used when a department has no flavour. */
  department?: string;
  name: string;
  widgets: LayoutWidgetPos[];
}

const pos = (id: string, y: number, w = 12, h = 12): LayoutWidgetPos => ({ id, x: 0, y, w, h });

/**
 * Which surface family a department belongs to. `field` and the two canonical
 * redirects (accounts / hr) are handled in `dashboard-switch.ts`, not here.
 */
export const SURFACE_FAMILY_BY_DEPARTMENT: Record<string, string> = {
  sales: 'sales',
  general_manager: 'sales',
  management: 'sales',
  it: 'it',
  digital_marketing: 'marketing',
  dispatch: 'delivery',
  production: 'delivery',
  warehouse: 'delivery',
  logistics: 'delivery',
  store: 'delivery',
  operations: 'delivery',
  support: 'support',
  customer_support: 'support',
};

type Level = ViewLevel;

const SURFACE_FOR: Record<string, Record<Level, { surface: string; name: string }>> = {
  sales: {
    own: { surface: 'sales.own.surface', name: 'My Dashboard' },
    department: { surface: 'sales.department.surface', name: 'Team Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
  it: {
    own: { surface: 'it.own.surface', name: 'My Dashboard' },
    // IT's own team screen is still its admin page; nothing sales-flavoured is
    // imposed on it.
    department: { surface: 'it.department.surface', name: 'IT Team Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
  marketing: {
    own: { surface: 'marketing.own.surface', name: 'My Dashboard' },
    department: { surface: 'marketing.department.surface', name: 'Marketing Team Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
  // Dispatch, production, warehouse, logistics and support work one queue at
  // every level today; that stays true and is recorded as data, not branching.
  delivery: {
    own: { surface: 'delivery.surface', name: 'Dispatch Dashboard' },
    department: { surface: 'delivery.surface', name: 'Dispatch Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
  support: {
    own: { surface: 'support.surface', name: 'Support Dashboard' },
    department: { surface: 'support.surface', name: 'Support Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
  base: {
    own: { surface: 'base.own.surface', name: 'My Dashboard' },
    department: { surface: 'base.department.surface', name: 'Team Dashboard' },
    company: { surface: 'sales.company.surface', name: 'Company Dashboard' },
  },
};

export function surfaceFamilyFor(department?: string | null): string {
  return SURFACE_FAMILY_BY_DEPARTMENT[String(department ?? '').trim().toLowerCase()] ?? 'base';
}

/**
 * Department decides the flavour; level decides the floor. An unknown
 * department gets its LEVEL's base family — never another department's surface,
 * which is how a dispatch account previously landed on an empty Sales pipeline
 * screen with a 403 on /api/deals.
 */
export function dashboardLayoutFor(level: Level, department?: string | null): DashboardViewLayout {
  const family = surfaceFamilyFor(department);
  const known = SURFACE_FAMILY_BY_DEPARTMENT[String(department ?? '').trim().toLowerCase()];
  const entry = SURFACE_FOR[family][level];
  const widgets = [pos(entry.surface, 0)];
  // The one tile proved live in this phase that has no source yet: it renders
  // `unavailable` instead of a zero, which is the point of the contract.
  if (family === 'sales' && level === 'own') widgets.push(pos('sales.own.incentives', 12, 12, 2));
  return {
    id: `${family}.${level}`,
    level,
    department: known ? String(department).trim().toLowerCase() : undefined,
    name: entry.name,
    widgets,
  };
}

export const layoutWidgetIds = (layout: DashboardViewLayout): string[] => layout.widgets.map((w) => w.id);
